/**
 * Report workbench (`/client-reports/:id`).
 *
 * Renders the sandboxed report viewer in BOTH view and edit mode, a version
 * dropdown + edit-journal, an "Advanced" raw-HTML escape hatch, and the
 * approve / reject / request-changes decision panel. Edit-mode text edits are
 * serialized in the iframe and stripped (report-edit-serialize) before POSTing
 * a new version; the server re-sanitizes on top.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import Popup from '../../../ui/popup/Popup';
import { RoleGate } from '../../../lib/rbac';
import { setMessage } from '../../../store/reducers/appSlice';
import type { RootState } from '../../../store/store';
import { serializeCleanHtml } from '../../../lib/report/report-edit-serialize';
import {
  getClientReport,
  getClientReportHtml,
  createReportVersion,
  type ClientReportDetail,
  type ClientReportVersion,
} from '../../../lib/api/client-reports/client-reports-api';
import { getReportPreviewVars } from '../../../lib/api/client-reports/report-branding-api';
import ReportEditorFrame, {
  type ReportEditorFrameHandle,
} from './ReportEditorFrame';
import { statusTagLabel } from '../reports-list-helpers';
import {
  auditActionLabel,
  auditActorLabel,
  formatDateTime,
  RAW_EDIT_SUMMARY,
  restoredSummary,
  versionOptionLabel,
  versionRowLabel,
  versionSubLabel,
  workbenchErrorMessage,
} from './workbench-labels';
import '../client-reports.scss';

const EDITABLE_STATUSES = new Set(['pending_review', 'changes_requested']);

const ReportWorkbench = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  // The loaders and save handlers read the translator through a ref, so a
  // language switch never re-fetches the report or reloads the frame.
  const tRef = useRef(t);
  tRef.current = t;

  // Resolve a brand display name from the scoped identity (fall back to slug).
  const accessibleShops = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity?.accessible_shops || []
  );
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );

  const [report, setReport] = useState<ClientReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [htmlError, setHtmlError] = useState('');
  const [htmlRetryNonce, setHtmlRetryNonce] = useState(0);

  // The agency's report colour tokens for this report's brand; null while
  // they load. The frame waits for them so it is built once, with the tokens,
  // instead of reloading (and dropping edits) when they arrive.
  const [previewVars, setPreviewVars] = useState<Record<string, string> | null>(
    null
  );

  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [dirty, setDirty] = useState(0);
  const [editSummary, setEditSummary] = useState('');
  const [saving, setSaving] = useState(false);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedHtml, setAdvancedHtml] = useState('');
  const [advancedConfirm, setAdvancedConfirm] = useState(false);
  const [advancedSaving, setAdvancedSaving] = useState(false);

  const [restoreTarget, setRestoreTarget] =
    useState<ClientReportVersion | null>(null);
  const [restoring, setRestoring] = useState(false);

  const frameRef = useRef<ReportEditorFrameHandle>(null);

  const brandName = useMemo(() => {
    const slug = report?.shop_slug;
    if (!slug) return t('ReportWorkbenchThisBrand', 'this brand');
    const fromAccessible = accessibleShops.find(
      (s) => (s.slug ?? s.brand_slug) === slug
    );
    if (fromAccessible)
      return fromAccessible.name ?? fromAccessible.brand_name ?? slug;
    const fromAll = allShops.find((s: any) => s?.slug === slug);
    return fromAll?.name || slug;
  }, [report, accessibleShops, allShops, t]);

  const currentVersionNumber = useMemo(() => {
    if (!report) return null;
    const cur = report.versions.find((v) => v.id === report.current_version_id);
    if (cur) return cur.version_number;
    return report.versions.length
      ? Math.max(...report.versions.map((v) => v.version_number))
      : null;
  }, [report]);

  const fetchDetail = useCallback(
    async (selectVersion?: number) => {
      try {
        const detail = await getClientReport(id);
        setReport(detail);
        setLoadError('');
        const cur = detail.versions.find(
          (v) => v.id === detail.current_version_id
        );
        const fallback = detail.versions.length
          ? Math.max(...detail.versions.map((v) => v.version_number))
          : null;
        setSelectedVersion(selectVersion ?? cur?.version_number ?? fallback);
      } catch (err: any) {
        setLoadError(workbenchErrorMessage('load', err, tRef.current));
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    setLoading(true);
    fetchDetail();
  }, [fetchDetail]);

  // Load the agency-level branding tokens once the report is loaded (a
  // refetch of the report detail after a save keeps the loaded tokens).
  const reportLoaded = report != null;
  useEffect(() => {
    if (!reportLoaded) return;
    let cancelled = false;
    setPreviewVars(null);
    getReportPreviewVars().then((vars) => {
      if (!cancelled) setPreviewVars(vars);
    });
    return () => {
      cancelled = true;
    };
  }, [reportLoaded]);

  // Load the HTML for the selected version (view mode only refetches; edit mode
  // keeps its live DOM).
  useEffect(() => {
    if (selectedVersion == null || mode === 'edit') return;
    let cancelled = false;
    setHtmlLoading(true);
    setHtmlError('');
    getClientReportHtml(id, selectedVersion)
      .then((h) => {
        if (!cancelled) setHtml(h);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setHtml(null);
          setHtmlError(workbenchErrorMessage('content', err, tRef.current));
        }
      })
      .finally(() => {
        if (!cancelled) setHtmlLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, selectedVersion, mode, htmlRetryNonce]);

  const isEditableStatus = report
    ? EDITABLE_STATUSES.has(String(report.status))
    : false;
  const viewingCurrent =
    selectedVersion != null && selectedVersion === currentVersionNumber;
  const canEnterEdit = isEditableStatus && viewingCurrent && html != null;

  const enterEdit = () => {
    setEditSummary('');
    setDirty(0);
    setMode('edit');
  };

  const exitEdit = () => {
    setMode('view');
    setDirty(0);
    // Force a fresh HTML load of the current version.
    setSelectedVersion((v) => v);
  };

  // Save flow: ask the iframe to serialize → onSerialized fires → strip + POST.
  const handleSaveClick = () => {
    if (saving) return;
    setSaving(true);
    frameRef.current?.requestSerialize();
  };

  const handleSerialized = useCallback(
    async (rawHtml: string) => {
      if (!saving) return; // ignore stray serializations
      try {
        const clean = serializeCleanHtml(rawHtml);
        const res = await createReportVersion(
          id,
          clean,
          editSummary.trim() || undefined
        );
        dispatch(
          setMessage({
            message: tRef.current(
              'ReportWorkbenchSavedVersion',
              'Saved version {{number}}.',
              { number: res.data.version_number }
            ),
            messageType: 'success',
          })
        );
        setMode('view');
        setDirty(0);
        await fetchDetail(res.data.version_number);
      } catch (err: any) {
        dispatch(
          setMessage({
            message: workbenchErrorMessage('save', err, tRef.current),
            messageType: 'error',
          })
        );
      } finally {
        setSaving(false);
      }
    },
    [saving, id, editSummary, dispatch, fetchDetail]
  );

  const handleRevert = () => frameRef.current?.revertAll();

  // Advanced raw-HTML escape hatch: load the current HTML into the textarea
  // when opened, save posts it verbatim as a new version (server sanitizes).
  const openAdvanced = () => {
    setAdvancedHtml(html || '');
    setAdvancedOpen(true);
  };

  const submitAdvanced = async () => {
    setAdvancedSaving(true);
    try {
      const res = await createReportVersion(
        id,
        advancedHtml,
        editSummary.trim() || RAW_EDIT_SUMMARY
      );
      dispatch(
        setMessage({
          message: t(
            'ReportWorkbenchSavedVersion',
            'Saved version {{number}}.',
            {
              number: res.data.version_number,
            }
          ),
          messageType: 'success',
        })
      );
      setAdvancedConfirm(false);
      setAdvancedOpen(false);
      await fetchDetail(res.data.version_number);
    } catch (err: any) {
      dispatch(
        setMessage({
          message: workbenchErrorMessage('rawSave', err, t),
          messageType: 'error',
        })
      );
    } finally {
      setAdvancedSaving(false);
    }
  };

  // Restore an older version as a NEW version (immutable history).
  const submitRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const olderHtml = await getClientReportHtml(
        id,
        restoreTarget.version_number
      );
      const res = await createReportVersion(
        id,
        olderHtml,
        restoredSummary(restoreTarget.version_number)
      );
      dispatch(
        setMessage({
          message: t(
            'ReportWorkbenchRestoredToast',
            'Restored version {{from}} as v{{to}}.',
            {
              from: restoreTarget.version_number,
              to: res.data.version_number,
            }
          ),
          messageType: 'success',
        })
      );
      setRestoreTarget(null);
      await fetchDetail(res.data.version_number);
    } catch (err: any) {
      dispatch(
        setMessage({
          message: workbenchErrorMessage('restore', err, t),
          messageType: 'error',
        })
      );
    } finally {
      setRestoring(false);
    }
  };

  // ---------------- render ----------------

  if (loading) {
    return (
      <SubPageInnerContentHolder
        title={t('ClientReports', 'Reporting')}
        backBtnLink='/client-reports'
      >
        <div className='report-workbench__loading'>
          <LoadingSpinner
            customLoadingMsg={t('ReportWorkbenchLoading', 'Loading report…')}
          />
        </div>
      </SubPageInnerContentHolder>
    );
  }

  if (loadError || !report) {
    return (
      <SubPageInnerContentHolder
        title={t('ClientReports', 'Reporting')}
        backBtnLink='/client-reports'
      >
        <div className='report-workbench__error'>
          {loadError || t('ReportWorkbenchNotFound', 'Report not found.')}
        </div>
      </SubPageInnerContentHolder>
    );
  }

  const statusLabel = statusTagLabel(String(report.status), t);
  const versionsDesc = [...report.versions].sort(
    (a, b) => b.version_number - a.version_number
  );

  return (
    <SubPageInnerContentHolder
      title={t('ReportWorkbenchTitle', 'Report workbench')}
      backBtnLink='/client-reports'
    >
      <div className='report-workbench'>
        {/* Top bar: title, status, version + edit controls. */}
        <div className='report-workbench__topbar'>
          <div className='report-workbench__title-block'>
            <h2 className='report-workbench__title'>{report.title}</h2>
            <div className='report-workbench__subtitle'>
              <span className='client-reports__brand-chip'>{brandName}</span>
              <span>{report.period_label}</span>
              <span
                className={`client-reports__tag client-reports__tag--${report.status}`}
              >
                {statusLabel}
              </span>
              <span>
                {report.created_by_type === 'ai'
                  ? t('ReportWorkbenchAiGenerated', 'AI-generated')
                  : t('ReportWorkbenchEmployeeAuthored', 'Employee-authored')}
              </span>
            </div>
          </div>

          <div className='report-workbench__controls'>
            {mode === 'view' ? (
              <>
                <select
                  className='report-workbench__version-select'
                  value={selectedVersion ?? ''}
                  onChange={(e) => setSelectedVersion(Number(e.target.value))}
                  aria-label={t(
                    'ReportWorkbenchVersionSelectLabel',
                    'Report version'
                  )}
                >
                  {versionsDesc.map((v) => (
                    <option key={v.id} value={v.version_number}>
                      {versionOptionLabel(
                        v,
                        v.version_number === currentVersionNumber,
                        t
                      )}
                    </option>
                  ))}
                </select>
                <RoleGate capability='portal.reports.review'>
                  {canEnterEdit && (
                    <MainSquareButton
                      type='button'
                      value={t('ReportWorkbenchEdit', 'Edit')}
                      buttonType='light'
                      normal
                      onClick={enterEdit}
                    />
                  )}
                </RoleGate>
              </>
            ) : (
              <>
                <span className='report-workbench__dirty'>
                  {dirty === 0
                    ? t('ReportWorkbenchNoUnsavedEdits', 'No unsaved edits')
                    : t('ReportWorkbenchUnsavedEdits', {
                        count: dirty,
                        defaultValue_one: '{{count}} unsaved edit',
                        defaultValue_other: '{{count}} unsaved edits',
                      })}
                </span>
                <input
                  className='report-workbench__version-select'
                  placeholder={t(
                    'ReportWorkbenchEditSummaryPlaceholder',
                    'Edit summary (optional)'
                  )}
                  value={editSummary}
                  onChange={(e) => setEditSummary(e.target.value)}
                  aria-label={t(
                    'ReportWorkbenchEditSummaryLabel',
                    'Edit summary'
                  )}
                />
                <MainSquareButton
                  type='button'
                  value={t('ReportWorkbenchRevert', 'Revert')}
                  buttonType='white'
                  normal
                  disabled={dirty === 0 || saving}
                  onClick={handleRevert}
                />
                <MainSquareButton
                  type='button'
                  value={t('ReportWorkbenchSaveVersion', 'Save version')}
                  buttonType='success'
                  normal
                  loading={saving}
                  disabled={saving}
                  onClick={handleSaveClick}
                />
                <MainSquareButton
                  type='button'
                  value={t('ReportWorkbenchCancel', 'Cancel')}
                  buttonType='white'
                  normal
                  disabled={saving}
                  onClick={exitEdit}
                />
              </>
            )}
          </div>
        </div>

        <div className='report-workbench__body'>
          {/* Frame */}
          <div>
            {!htmlLoading && htmlError ? (
              <div className='report-workbench__frame-wrap'>
                <div className='report-workbench__error'>
                  <p>{htmlError}</p>
                  <MainSquareButton
                    type='button'
                    value={t('ReportWorkbenchRetry', 'Retry')}
                    buttonType='light'
                    normal
                    onClick={() => setHtmlRetryNonce((n) => n + 1)}
                  />
                </div>
              </div>
            ) : htmlLoading || html == null || previewVars == null ? (
              <div className='report-workbench__frame-wrap'>
                <div className='report-workbench__loading'>
                  <LoadingSpinner
                    customLoadingMsg={t(
                      'ReportWorkbenchLoading',
                      'Loading report…'
                    )}
                  />
                </div>
              </div>
            ) : (
              <ReportEditorFrame
                ref={frameRef}
                html={html}
                mode={mode}
                features={{ deep_dive: true, changelog: false }}
                theme='light'
                reportVars={previewVars}
                title={report.title}
                onEditState={(d) => setDirty(d)}
                onSerialized={handleSerialized}
              />
            )}

            {/* Advanced raw-HTML escape hatch. */}
            <RoleGate capability='portal.reports.review'>
              {mode === 'view' && (
                <details
                  className='report-workbench__advanced'
                  style={{ marginTop: '1.4rem' }}
                  open={advancedOpen}
                >
                  <summary
                    className='report-workbench__advanced-summary'
                    onClick={(e) => {
                      e.preventDefault();
                      if (advancedOpen) setAdvancedOpen(false);
                      else openAdvanced();
                    }}
                  >
                    {t(
                      'ReportWorkbenchAdvancedSummary',
                      'Advanced — edit raw HTML'
                    )}
                  </summary>
                  {advancedOpen && (
                    <>
                      <textarea
                        className='report-workbench__advanced-textarea'
                        value={advancedHtml}
                        onChange={(e) => setAdvancedHtml(e.target.value)}
                        spellCheck={false}
                      />
                      <div className='report-workbench__confirm-actions'>
                        <MainSquareButton
                          type='button'
                          value={t(
                            'ReportWorkbenchSaveAsNewVersion',
                            'Save as new version'
                          )}
                          buttonType='warning'
                          normal
                          onClick={() => setAdvancedConfirm(true)}
                        />
                      </div>
                    </>
                  )}
                </details>
              )}
            </RoleGate>
          </div>

          {/* Side rail: decision panel, versions/journal, audit. */}
          <div className='report-workbench__rail'>
            <div className='report-workbench__panel'>
              <h3 className='report-workbench__panel-title'>
                {t('ReportWorkbenchApprovalsTitle', 'Approvals')}
              </h3>
              <p className='report-workbench__compat'>
                {t(
                  'ReportWorkbenchApprovalsBody',
                  'Approvals happen in the Action queue. Review and edit the report here, then approve or request changes from the queue.'
                )}
              </p>
              <div className='report-workbench__decision-actions'>
                <MainSquareButton
                  type='button'
                  value={t('ReportWorkbenchGoToQueue', 'Go to Action queue')}
                  buttonType='light'
                  normal
                  onClick={() => navigate('/actions/queue')}
                />
              </div>
            </div>

            <div className='report-workbench__panel'>
              <h3 className='report-workbench__panel-title'>
                {t('ReportWorkbenchVersionsTitle', 'Versions & edit journal')}
              </h3>
              <div className='report-workbench__versions'>
                {versionsDesc.map((v) => (
                  <div
                    key={v.id}
                    className={`report-workbench__version-row${
                      v.version_number === selectedVersion
                        ? ' report-workbench__version-row--active'
                        : ''
                    }`}
                  >
                    <div className='report-workbench__version-meta'>
                      <div className='report-workbench__version-num'>
                        {versionRowLabel(v, t)}
                      </div>
                      <div className='report-workbench__version-sub'>
                        {versionSubLabel(v, i18n.language, t)}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.3rem',
                      }}
                    >
                      {v.version_number !== selectedVersion && (
                        <button
                          type='button'
                          className='report-workbench__linkbtn'
                          onClick={() => setSelectedVersion(v.version_number)}
                        >
                          {t('ReportWorkbenchView', 'View')}
                        </button>
                      )}
                      <RoleGate capability='portal.reports.review'>
                        {isEditableStatus &&
                          v.version_number !== currentVersionNumber && (
                            <button
                              type='button'
                              className='report-workbench__linkbtn'
                              onClick={() => setRestoreTarget(v)}
                            >
                              {t('ReportWorkbenchRestore', 'Restore')}
                            </button>
                          )}
                      </RoleGate>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className='report-workbench__panel'>
              <h3 className='report-workbench__panel-title'>
                {t('ReportWorkbenchActivityTitle', 'Activity')}
              </h3>
              <div className='report-workbench__audit'>
                {report.audit_tail.length === 0 ? (
                  <p className='report-workbench__compat'>
                    {t('ReportWorkbenchNoActivity', 'No activity yet.')}
                  </p>
                ) : (
                  report.audit_tail.map((a) => (
                    <div key={a.id} className='report-workbench__audit-row'>
                      <span className='report-workbench__audit-action'>
                        {auditActionLabel(a.action, t)}
                      </span>
                      <span>({auditActorLabel(a.actor_type, t)})</span>
                      <span className='report-workbench__audit-time'>
                        {formatDateTime(a.created_at, i18n.language)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced raw-HTML save confirm. */}
      <Popup
        trigger={advancedConfirm}
        setTrigger={() => {
          if (!advancedSaving) setAdvancedConfirm(false);
        }}
      >
        <div className='report-workbench__confirm'>
          <h3 className='report-workbench__confirm-title'>
            {t('ReportWorkbenchRawConfirmTitle', 'Save raw HTML?')}
          </h3>
          <p className='report-workbench__confirm-text'>
            {t(
              'ReportWorkbenchRawConfirmBody',
              'You are saving hand-edited HTML as a new version. The server re-sanitizes it, but structural mistakes can break the layout. This bypasses the guided editor — proceed only if you know the report markup.'
            )}
          </p>
          <div className='report-workbench__confirm-actions'>
            <MainSquareButton
              type='button'
              value={t('ReportWorkbenchCancel', 'Cancel')}
              buttonType='white'
              normal
              disabled={advancedSaving}
              onClick={() => setAdvancedConfirm(false)}
            />
            <MainSquareButton
              type='button'
              value={t('ReportWorkbenchSaveRawHtml', 'Save raw HTML')}
              buttonType='warning'
              normal
              loading={advancedSaving}
              disabled={advancedSaving}
              onClick={submitAdvanced}
            />
          </div>
        </div>
      </Popup>

      {/* Restore-version confirm. */}
      <Popup
        trigger={!!restoreTarget}
        setTrigger={() => {
          if (!restoring) setRestoreTarget(null);
        }}
      >
        {restoreTarget && (
          <div className='report-workbench__confirm'>
            <h3 className='report-workbench__confirm-title'>
              {t(
                'ReportWorkbenchRestoreConfirmTitle',
                'Restore version {{number}}?',
                { number: restoreTarget.version_number }
              )}
            </h3>
            <p className='report-workbench__confirm-text'>
              {t(
                'ReportWorkbenchRestoreConfirmBody',
                "This copies version {{number}}'s content into a brand-new version (history is never overwritten).",
                { number: restoreTarget.version_number }
              )}
            </p>
            <div className='report-workbench__confirm-actions'>
              <MainSquareButton
                type='button'
                value={t('ReportWorkbenchCancel', 'Cancel')}
                buttonType='white'
                normal
                disabled={restoring}
                onClick={() => setRestoreTarget(null)}
              />
              <MainSquareButton
                type='button'
                value={t(
                  'ReportWorkbenchRestoreAsNewVersion',
                  'Restore as new version'
                )}
                buttonType='light'
                normal
                loading={restoring}
                disabled={restoring}
                onClick={submitRestore}
              />
            </div>
          </div>
        )}
      </Popup>
    </SubPageInnerContentHolder>
  );
};

export default ReportWorkbench;
